(function (root, factory) {
  const api = factory(root);
  const app = root.App = root.App || {};
  app.i18n = api;
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (root) {
  'use strict';

  const app = root.App = root.App || {};
  const localeApi = app.locale || (function resolveLocaleApi() {
    if (typeof module === 'object' && module.exports) {
      try {
        return require('./locale.js');
      } catch (_) {
        return null;
      }
    }
    return null;
  })();
  const DEFAULT_LOCALE = localeApi?.DEFAULT_LOCALE || 'en';

  function getDocument() {
    return root.document || root.window?.document || null;
  }

  function getState() {
    return app.state || root.window?.App?.state || null;
  }

  function normalizeLocale(value) {
    if (typeof localeApi?.normalizeLocale === 'function') {
      return localeApi.normalizeLocale(value);
    }
    return typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : DEFAULT_LOCALE;
  }

  function getLocale() {
    return normalizeLocale(getState()?.identity?.locale || DEFAULT_LOCALE);
  }

  function getStrings() {
    return app.strings || {};
  }

  function getNestedValue(obj, pathParts) {
    let current = obj;
    for (const part of pathParts) {
      if (!current || typeof current !== 'object' || !Object.prototype.hasOwnProperty.call(current, part)) {
        return undefined;
      }
      current = current[part];
    }
    return current;
  }

  function interpolate(template, params) {
    if (typeof template !== 'string' || !params || typeof params !== 'object') {
      return template;
    }
    return template.replace(/\{([A-Za-z0-9_]+)\}/g, (match, key) => {
      if (!Object.prototype.hasOwnProperty.call(params, key)) {
        return match;
      }
      const value = params[key];
      return value == null ? '' : String(value);
    });
  }

  function resolveMessage(key, locale) {
    const normalizedKey = typeof key === 'string' ? key.trim() : '';
    if (!normalizedKey) {
      return undefined;
    }
    const parts = normalizedKey.split('.');
    const bundleKey = parts.shift();
    if (!bundleKey) {
      return undefined;
    }
    const bundle = getStrings()[bundleKey];
    if (!bundle || typeof bundle !== 'object') {
      return undefined;
    }
    const localized = getNestedValue(bundle[locale] || bundle[DEFAULT_LOCALE] || {}, parts);
    if (localized !== undefined) {
      return localized;
    }
    return getNestedValue(bundle[DEFAULT_LOCALE] || {}, parts);
  }

  function t(key, params, options = {}) {
    const locale = normalizeLocale(options.locale || getLocale());
    const message = resolveMessage(key, locale);
    if (message === undefined) {
      return Object.prototype.hasOwnProperty.call(options, 'fallback') ? options.fallback : key;
    }
    return interpolate(message, params);
  }

  function applyDocumentLocale(locale) {
    const doc = getDocument();
    if (!doc || !doc.documentElement || typeof doc.documentElement.setAttribute !== 'function') {
      return;
    }
    doc.documentElement.setAttribute('lang', locale);
  }

  function parseParams(raw) {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  function translateElement(element, locale) {
    if (!element || typeof element.getAttribute !== 'function') {
      return;
    }
    const params = parseParams(element.getAttribute('data-i18n-params'));
    const textKey = element.getAttribute('data-i18n');
    if (textKey) {
      element.textContent = t(textKey, params, { locale });
    }
    const placeholderKey = element.getAttribute('data-i18n-placeholder');
    if (placeholderKey) {
      element.setAttribute('placeholder', t(placeholderKey, params, { locale }));
    }
    const titleKey = element.getAttribute('data-i18n-title');
    if (titleKey) {
      element.setAttribute('title', t(titleKey, params, { locale }));
    }
    const ariaKey = element.getAttribute('data-i18n-aria-label');
    if (ariaKey) {
      element.setAttribute('aria-label', t(ariaKey, params, { locale }));
    }
    const valueKey = element.getAttribute('data-i18n-value');
    if (valueKey) {
      element.setAttribute('value', t(valueKey, params, { locale }));
    }
    const tooltipKey = element.getAttribute('data-i18n-tooltip');
    if (tooltipKey) {
      element.setAttribute('data-tooltip', t(tooltipKey, params, { locale }));
    }
  }

  function apply(target) {
    const locale = getLocale();
    const rootNode = target || getDocument();
    if (!rootNode) {
      return;
    }
    if (typeof rootNode.getAttribute === 'function') {
      translateElement(rootNode, locale);
    }
    if (typeof rootNode.querySelectorAll !== 'function') {
      return;
    }
    const nodes = rootNode.querySelectorAll('[data-i18n], [data-i18n-placeholder], [data-i18n-title], [data-i18n-aria-label], [data-i18n-value], [data-i18n-tooltip]');
    nodes.forEach((node) => translateElement(node, locale));
  }

  function setLocale(locale, options = {}) {
    const nextLocale = typeof localeApi?.persistLocale === 'function'
      ? localeApi.persistLocale(locale, {
        identity: getState()?.identity,
        storage: root.localStorage || root.window?.localStorage,
        doc: getDocument(),
      })
      : normalizeLocale(locale);
    const state = getState();
    if (state?.identity) {
      state.identity.locale = nextLocale;
    }
    applyDocumentLocale(nextLocale);
    if (options.apply !== false) {
      apply(options.target || getDocument());
    }
    try {
      app.bus?.dispatchEvent?.(new root.CustomEvent('locale:changed', { detail: { locale: nextLocale } }));
    } catch (_) {
      // Ignore bus dispatch issues in test environments without CustomEvent.
    }
    return nextLocale;
  }

  function init() {
    const locale = getLocale();
    applyDocumentLocale(locale);
    apply(getDocument());
    localeApi?.syncServerLocale?.(locale);
  }

  return {
    getLocale,
    setLocale,
    t,
    apply,
    init,
    __test: {
      interpolate,
      resolveMessage,
      translateElement,
    },
  };
});
